/**
 * Aplicación web Directorio del Ayuntamiento de Zamora.
 * 
 * Desarrollo e implementación: Jaime León Pereira.
 * Ingeniero Informático de la Unidad TIC.
 */
import logo from "../../assets/logoAyto.png";
import { useMsal } from "@azure/msal-react";
import { BsPersonCircle } from "react-icons/bs";
import { useEffect } from "react";

/**
 * Componente de barra de navegación principal.
 * 
 * Barra de navegación responsiva con:
 * - Logo del Ayuntamiento
 * - Menú hamburguesa en móvil/tablet
 * - Título central en escritorio
 * - Menú de usuario con opción de logout
 */

const Navbar = ({ isMobile, isTablet, onMenuClick }) => {
  /* ============================
      Autenticación (MSAL)
     ============================ */
  const { instance, accounts } = useMsal();

  /**
   * Maneja el cierre de sesión del usuario.
   * Redirige al endpoint de logout de Microsoft Entra.
   */
  const handleLogout = () => {
    instance.logoutRedirect({ postLogoutRedirectUri: window.location.origin });
  };

  // Obtener nombre del usuario autenticado
  const userName = accounts && accounts.length > 0 ? accounts[0].name : "Usuario";

  /* ============================
     Gestión del menú de usuario
     ============================ */
  
  /**
   * Efecto para cerrar el menú de usuario al hacer click fuera.
   * Se ejecuta solo una vez al montar el componente.
   */
  useEffect(() => {
    const handleClickOutside = (event) => {
      const menu = document.getElementById("user-menu-pro");
      const button = document.querySelector('[aria-label="Abrir menú de usuario"]');
       
      // Si el menú está abierto y se hace click fuera
      if (
        menu &&
        menu.style.opacity === "1" &&
        !menu.contains(event.target) &&
        button &&
        !button.contains(event.target)
      ) {
        // Cerrar el menú
        menu.style.opacity = "0";
        menu.style.transform = "translateY(-6px) scale(0.96)";
        menu.style.pointerEvents = "none";
      }
    };

    // Registrar listener global
    document.addEventListener("mousedown", handleClickOutside);
    
    // Cleanup al desmontar
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  
  /*************************************************************
   *  Renderizado de la barra de navegación                    *
   *************************************************************/
  return (
    /* Barra de navegación principal */
    <nav className="navbar bg-white shadow mb-3">
      <div className="container d-flex align-items-center justify-content-between">

        {/* ==========================================
            Sección izquierda: Logo y menú hamburguesa
            ========================================== */}
        <div className="d-flex align-items-center gap-2">
          {/* Botón menú hamburguesa (solo móvil/tablet) */}
          {(isMobile || isTablet) &&(
            <button
              className="btn btn-outline-dark"
              onClick={onMenuClick}
              aria-label="Abrir menú de navegación"
            >
              <i className="bi bi-list fs-3"></i>
            </button>
          )}
          {/* Logo del Ayuntamiento */}
          <a href="/" className="navbar-brand d-flex align-items-center">
            <img
              src={logo}
              alt="Logo Ayuntamiento de Zamora"
              style={{ height: '50px', width: 'auto' }}
            />
          </a>
        </div>

        {/* =========================================
            Sección central: Título (solo escritorio)
            ========================================= */}
        <div className="d-none d-md-flex flex-grow-1 justify-content-center">
          <span className="fw-bold fs-5 text-center">
            Directorio de Personal
          </span>
        </div>

        {/* ================================
            Sección derecha: Menú de usuario
            ================================ */}
        <div style={{ position: "relative" }}>
          <button
            onClick={() => {
              const menu = document.getElementById("user-menu-pro");
              const open = menu.style.opacity === "1";
              // Alternar estado del menú
              menu.style.opacity = open ? "0" : "1";
              menu.style.transform = open ? "translateY(-6px) scale(0.96)" : "translateY(0) scale(1)";
              menu.style.pointerEvents = open ? "none" : "auto";
            }}
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              width: "44px",
              height: "44px",
              borderRadius: "14px",
              background: "linear-gradient(145deg, #222, #2d2d2d)",
              border: "1px solid rgba(255,255,255,0.18)",
              color: "white",
              cursor: "pointer",
              boxShadow: "0 6px 16px rgba(0,0,0,0.35)",
              transition: "all 0.25s ease",
            }}
            aria-label="Abrir menú de usuario"
          >
            <BsPersonCircle size={22} />
          </button>
          
          {/* Menú desplegable de usuario */}
          <div
            id="user-menu-pro"
            style={{
              position: "absolute",
              right: 0,
              marginTop: "0.7rem",
              minWidth: "200px",
              background: "rgba(255,255,255,0.9)",
              backdropFilter: "blur(16px) saturate(160%)",
              borderRadius: "16px",
              border: "1px solid rgba(0,0,0,0.15)",
              boxShadow: "0 18px 40px rgba(0,0,0,0.25)",
              padding: "0.5rem 0",
              opacity: 0,
              transform: "translateY(-6px) scale(0.96)",
              transition: "all 0.25s cubic-bezier(.4,0,.2,1)",
              pointerEvents: "none",
              zIndex: 999,
            }}
          >
             {/* Nombre del usuario */}
            <div
              style={{
                padding: "0.8rem 1rem",
                textAlign: "center",
                fontWeight: 700,
                fontSize: "0.95rem",
                color: "#111",
              }}
            >
              {userName}
            </div>
            {/* Separador */}
            <hr style={{ margin: "4px 0", borderColor: "rgba(0,0,0,0.1)" }} />

            {/* Opción de logout de Microsoft */}
            <div
              onClick={handleLogout}
              style={{
                padding: "0.7rem 1rem",
                cursor: "pointer",
                display: "flex",
                alignItems: "center",
                gap: "8px",
                fontSize: "0.9rem",
                fontWeight: 500,
                color: "#222",
                transition: "all 0.2s ease",
              }}
              onMouseEnter={(e) => (e.currentTarget.style.background = "rgba(0,0,0,0.05)")}
              onMouseLeave={(e) => (e.currentTarget.style.background = "transparent")}
              aria-label="Cerrar sesión"
            >
              ⎋ Cerrar sesión
            </div>
          </div>
        </div>
      </div>
    </nav>
  );
};

export default Navbar;



